import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PublicLayout } from '../components/PublicLayout';
import {
  ArrowRight, ChevronRight, Shield, Award, Globe, Users,
  Building2, MapPin, Trophy, Star, CheckCircle, Calendar, Medal
} from 'lucide-react';

const RSS_LOGO = "https://customer-assets.emergentagent.com/job_199c36c2-e07b-4567-84a2-e99d8a5503d6/artifacts/c9vy7sfe_rumpun_silat_sarawak_logo_transparent_1024.png";

const authorityStats = [
  { value: '9 Feb 1987', label: 'Tarikh Ditubuhkan', sub: '38 tahun berkhidmat' },
  { value: 'PPM-010', label: 'No. Pendaftaran', sub: 'PPM-010-13-02091987' },
  { value: '168', label: 'Perguruan Gabungan', sub: 'Seluruh Sarawak' },
  { value: '15,000', label: 'Anggaran Ahli', sub: 'Pesilat berdaftar' },
  { value: '13', label: 'Wilayah RSS', sub: 'Merentasi Sarawak' },
  { value: 'Diiktiraf', label: 'Kerajaan Negeri Sarawak', sub: '& PESAKA' },
];

const matlamat = [
  {
    num: '01', icon: <Users size={20} />,
    title: 'Menyatupadu Perguruan & Pesilat',
    desc: 'Menyatupadu dan mempererat hubungan di antara semua perguruan silat bumiputera dan pesilat di Sarawak.',
  },
  {
    num: '02', icon: <Shield size={20} />,
    title: 'Memaju Seni Silat Melayu',
    desc: 'Memperjuang, memaju dan menyelaraskan Seni Silat mempertahankan diri rumpun Melayu di Sarawak.',
  },
  {
    num: '03', icon: <Star size={20} />,
    title: 'Memelihara Warisan Bangsa',
    desc: 'Memelihara dan mempertahankan silat sebagai seni warisan bangsa yang perlu dijaga dan diwarisi.',
  },
  {
    num: '04', icon: <Award size={20} />,
    title: 'Membina Akhlak & Disiplin',
    desc: 'Membina akhlak, peribadi, disiplin, mutu dan daya tahan dalam setiap ahli dan pesilat.',
  },
  {
    num: '05', icon: <Globe size={20} />,
    title: 'Asas Ketahanan Nasional',
    desc: 'Menjadikan silat sebagai asas ketahanan nasional yang kukuh dengan ahli bermaruah, bersatu-padu, sihat dan berdisiplin.',
  },
];

const wilayahList = [
  'Kuching', 'Kota Samarahan', 'Serian', 'Sri Aman', 'Betong',
  'Sarikei', 'Sibu', 'Mukah', 'Kapit', 'Bintulu', 'Miri', 'Limbang', 'Lawas',
];

const kepimpinanPreview = [
  { position: 'Presiden', name: 'Tuan Haji Muhammad Haneef Ali', level: 'primary' },
  { position: 'Timbalan Presiden', name: "Dato' Haji Mohamad Yusnar", level: 'secondary' },
  { position: 'Naib Presiden I', name: 'Haji Mohamed Alli Bin Haji Zainudin', level: 'secondary' },
  { position: 'Naib Presiden II', name: 'Ismawi Bin Kedri', level: 'secondary' },
];

const biroData = [
  {
    name: 'Biro Kejurulatihan',
    ketua: 'Encik Hussien Bin Zawawi',
    color: '#D71920',
    desc: 'Program latihan, pensijilan jurulatih dan pengembangan kemahiran.',
  },
  {
    name: 'Biro Teknikal',
    ketua: 'Encik Awang Pathi Bin Awang Daud',
    color: '#D4AF37',
    desc: 'Teknikal pertandingan, penilaian kemahiran dan wasit-juri.',
  },
  {
    name: 'Biro Perhubungan Antarabangsa',
    ketua: "Dato' Haji Mohamad Yusnar",
    color: '#0B0B0B',
    desc: 'Hubungan antarabangsa dan perwakilan dalam kejuaraan dunia.',
  },
];

const sukmaHighlights = [
  { year: '1990', event: 'SUKMA III', result: 'Juara Keseluruhan', tier: 'gold' },
  { year: '1992', event: 'SUKMA IV', result: 'Juara Keseluruhan', tier: 'gold' },
  { year: '2016', event: 'SUKMA XVIII', result: 'Juara Keseluruhan', tier: 'gold' },
  { year: '2024', event: 'SUKMA XXI', result: 'Naib Juara', tier: 'silver' },
  { year: '2025', event: 'Kejuaraan Kebangsaan ke-21', result: '1E • 2P • 3G', tier: 'medals' },
];

const intlHighlights = [
  { year: '1992', event: 'Kejuaraan Silat Sedunia', venue: 'Jakarta', tier: 'gold' },
  { year: '1997', event: 'Sukan SEA XIX', venue: 'Jakarta', tier: 'silver' },
  { year: '2004', event: 'Kejuaraan Silat Sedunia', venue: 'Singapura', tier: 'gold' },
  { year: '2024', event: 'Silat Dunia ke-20', venue: 'Abu Dhabi', tier: 'gold' },
  { year: '2025', event: 'Pra Sukan SEA Games', venue: 'Thailand', tier: 'silver' },
];

const programBesar = [
  { year: '2000', title: 'Festival Pencak Silat Kepulauan Borneo dan Serantau', cat: 'Festival Antarabangsa' },
  { year: '2001', title: 'Konvensyen Guru-guru Silat Negeri Sarawak Ke-1', cat: 'Konvensyen' },
  { year: '2003', title: 'Festival Pencak Silat Antarabangsa & Konvensyen Ke-2', cat: 'Festival & Konvensyen' },
  { year: '2006', title: 'Konvensyen Guru-guru Ke-3 & Perasmian Ibu Pejabat RSS', cat: 'Konvensyen & Perasmian' },
  { year: '2010', title: 'Konvensyen Guru-guru Silat Negeri Sarawak Ke-4', cat: 'Konvensyen' },
  { year: '2020', title: 'Perhimpunan Guru-guru Silat Negeri Sarawak', cat: 'Perhimpunan' },
  { year: '2022', title: 'RSS Beach Games Mukah', cat: 'Sukan & Komuniti' },
  { year: '2023', title: 'Sarawak Premier International Silat Championship', cat: 'Kejuaraan Antarabangsa' },
];

const aktivitiCards = [
  { title: 'Kunjungan Hormat YTY Yang Dipertua Negeri Sarawak', cat: 'Hubungan Rasmi' },
  { title: 'Konvensyen Guru-Guru Silat', cat: 'Pendidikan' },
  { title: 'Sidang Agung Ke-15 RSS Sarawak', cat: 'Pentadbiran' },
  { title: 'Kursus Asas Pengenalan Silat', cat: 'Latihan' },
  { title: 'Sukan Malaysia XXI Sarawak 2024', cat: 'Kejuaraan' },
  { title: 'Ramah Tamah Aidilfitri Wilayah-Wilayah RSS', cat: 'Komuniti' },
];

const tierColor = {
  gold: { bg: '#FFFBF0', border: '#D4AF37', text: '#b8860b' },
  silver: { bg: '#F8F8F8', border: '#9ca3af', text: '#6b7280' },
  medals: { bg: '#FEF2F2', border: '#D71920', text: '#D71920' },
};

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [achievTab, setAchievTab] = useState('sukma');

  const toDashboard = () => navigate(user?.role === 'RSS_ADMIN' ? '/admin/dashboard' : '/school/dashboard');

  return (
    <PublicLayout>

      {/* ── 1. HERO ─────────────────────────────────────────────── */}
      <section
        className="bg-white relative overflow-hidden"
        data-testid="hero-section"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #E5E5E5 1px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Left: Text */}
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-[#D71920] tracking-widest uppercase mb-6 border border-[#D71920]/30 bg-white px-3 py-1.5 rounded-sm shadow-sm">
                Badan Induk Rasmi Persilatan Negeri Sarawak
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0B0B0B] leading-[1.08] mb-6"
                data-testid="hero-title"
              >
                Memartabatkan
                <span className="block text-[#D71920]">Warisan Seni</span>
                <span className="block">Silat Sarawak</span>
              </h1>
              <p
                className="text-[#555555] text-base sm:text-lg leading-relaxed mb-8 max-w-lg"
                data-testid="hero-subtitle"
              >
                Rumpun Silat Sarawak ialah badan induk rasmi persilatan Negeri Sarawak yang menyatukan perguruan,
                guru, jurulatih dan pesilat seluruh negeri dalam satu ekosistem warisan, disiplin dan pembangunan yang berwibawa.
              </p>
              <div className="flex flex-wrap gap-3" data-testid="hero-cta-buttons">
                <Link
                  to="/tentang-rss"
                  className="inline-flex items-center gap-2 bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-bold px-6 py-3 rounded transition-colors"
                  data-testid="hero-cta-kenali"
                >
                  Kenali RSS <ArrowRight size={14} />
                </Link>
                <Link
                  to="/direktori-perguruan"
                  className="inline-flex items-center gap-2 border-2 border-[#0B0B0B] text-[#0B0B0B] hover:bg-[#0B0B0B] hover:text-white text-sm font-bold px-6 py-3 rounded transition-colors"
                  data-testid="hero-cta-direktori"
                >
                  Direktori Perguruan
                </Link>
                {user ? (
                  <button
                    onClick={toDashboard}
                    className="inline-flex items-center gap-2 border-2 border-[#E5E5E5] text-[#555] hover:border-[#0B0B0B] hover:text-[#0B0B0B] text-sm font-medium px-6 py-3 rounded transition-colors"
                    data-testid="hero-cta-dashboard"
                  >
                    Dashboard
                  </button>
                ) : (
                  <Link
                    to="/portal-ahli"
                    className="inline-flex items-center gap-2 border-2 border-[#E5E5E5] text-[#555] hover:border-[#0B0B0B] hover:text-[#0B0B0B] text-sm font-medium px-6 py-3 rounded transition-colors"
                    data-testid="hero-cta-portal"
                  >
                    Portal Ahli
                  </Link>
                )}
              </div>
              <div className="mt-10 pt-8 border-t border-[#E5E5E5]">
                <p className="text-xs font-extrabold tracking-[0.3em] text-[#D4AF37] uppercase">
                  Berseni · Berbudi · Berbakti
                </p>
              </div>
            </div>

            {/* Right: Logo Display */}
            <div className="order-1 lg:order-2 flex justify-center" data-testid="hero-logo-display">
              <div className="relative">
                <div className="absolute inset-0 rounded-full border-2 border-[#FFD21F]/25 scale-[1.12]" />
                <div className="absolute inset-0 rounded-full border border-[#D71920]/15 scale-[1.28]" />
                <div className="absolute inset-0 rounded-full border border-[#0B0B0B]/5 scale-[1.44]" />
                <div className="relative w-52 h-52 sm:w-72 sm:h-72 lg:w-80 lg:h-80 bg-white rounded-full flex items-center justify-center border border-[#E5E5E5] shadow-2xl">
                  <img src={RSS_LOGO} alt="Rumpun Silat Sarawak" className="w-4/5 h-4/5 object-contain" />
                </div>
                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-[#0B0B0B] text-white text-[10px] font-extrabold tracking-widest uppercase px-5 py-2 rounded-full whitespace-nowrap shadow-lg">
                  Ditubuhkan 9 Feb 1987
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. AUTHORITY BAR ─────────────────────────────────────── */}
      <section className="bg-[#0B0B0B] text-white" data-testid="authority-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-0 sm:divide-x sm:divide-white/8">
            {authorityStats.map((s, i) => (
              <div key={i} className="text-center sm:px-4 py-2" data-testid={`authority-stat-${i}`}>
                <div className="text-lg sm:text-xl font-extrabold text-[#FFD21F] leading-tight">{s.value}</div>
                <div className="text-[11px] font-bold text-white mt-0.5 leading-tight">{s.label}</div>
                <div className="text-[10px] text-[#666] mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. ABOUT RSS ─────────────────────────────────────────── */}
      <section className="bg-white py-20 sm:py-28" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-14 items-start">
            <div className="lg:col-span-3">
              <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-4">Tentang Organisasi</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B] leading-tight mb-6">
                Tentang Rumpun Silat Sarawak
              </h2>
              <div className="space-y-4 text-[#555] text-sm sm:text-base leading-relaxed">
                <p>
                  Rumpun Silat Sarawak (RSS) ialah badan induk rasmi persilatan Negeri Sarawak yang bertanggungjawab
                  merancang, menyelaras dan membangunkan seni silat di peringkat negeri. Ditubuhkan pada <strong className="text-[#0B0B0B]">9 Februari 1987</strong>,
                  RSS telah berkembang menjadi organisasi silat terbesar di Sarawak.
                </p>
                <p>
                  RSS diiktiraf oleh <strong className="text-[#0B0B0B]">Kerajaan Negeri Sarawak</strong> sebagai Badan Induk Rasmi
                  Persilatan Negeri Sarawak dan juga oleh <strong className="text-[#0B0B0B]">Persekutuan Silat Kebangsaan Malaysia (PESAKA)</strong>.
                  Penaung organisasi adalah YAB Premier Sarawak.
                </p>
                <p>
                  RSS berperanan menyumbang idea, kepakaran, tenaga dan program ke arah pembangunan komuniti yang
                  berdisiplin dan berkualiti, selaras dengan aspirasi dan pembangunan negeri Sarawak.
                </p>
              </div>
              <Link to="/tentang-rss" className="inline-flex items-center gap-2 mt-8 text-sm font-bold text-[#D71920] hover:gap-3 transition-all" data-testid="about-learn-more">
                Selanjutnya Tentang RSS <ArrowRight size={14} />
              </Link>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-[#0B0B0B] text-white rounded overflow-hidden">
                <div className="h-0.5 bg-gradient-to-r from-[#D71920] via-[#FFD21F] to-[#D71920]" />
                <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
                  <img src={RSS_LOGO} alt="RSS" className="h-10 w-10 object-contain" />
                  <div>
                    <div className="text-sm font-extrabold text-white">Rumpun Silat Sarawak</div>
                    <div className="text-[#FFD21F] text-[10px] font-bold tracking-widest uppercase">BERSENI BERBUDI BERBAKTI</div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { label: 'No. Pendaftaran (PPM)', value: 'PPM-010-13-02091987' },
                    { label: 'Tarikh Pendaftaran', value: '09 Februari 1987' },
                    { label: 'No. Pendaftaran (Sukan)', value: '4775/2011' },
                    { label: 'Pengiktirafan Badan Induk', value: '08 Januari 2020' },
                  ].map((r, i) => (
                    <div key={i} className="border border-white/8 rounded p-3">
                      <div className="text-[10px] text-[#999] mb-0.5">{r.label}</div>
                      <div className="text-xs font-bold text-[#FFD21F]">{r.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {[
                  { label: 'Diiktiraf PESAKA', sub: 'Persekutuan Silat Kebangsaan Malaysia' },
                  { label: 'Diiktiraf Kerajaan Negeri Sarawak', sub: 'Badan Induk Rasmi Persilatan' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[#F7F7F7] border border-[#E5E5E5] rounded">
                    <CheckCircle size={15} className="text-[#16a34a] flex-shrink-0" />
                    <div>
                      <div className="text-xs font-bold text-[#0B0B0B]">{b.label}</div>
                      <div className="text-xs text-[#777]">{b.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. MATLAMAT & TUJUAN ─────────────────────────────────── */}
      <section className="bg-[#F7F7F7] py-20 sm:py-28" data-testid="matlamat-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-3">Dasar Organisasi</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B] mb-4">Matlamat & Tujuan RSS</h2>
            <p className="text-[#555] text-sm sm:text-base leading-relaxed">
              Menjadikan silat sebagai maruah kebangsaan dan ketahanan nasional yang kukuh dengan ahli yang bermaruah,
              bersatu-padu, sihat dan berdisiplin.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {matlamat.map((m, i) => (
              <div
                key={i}
                className="bg-white border border-[#E5E5E5] p-6 rounded hover:border-[#D71920]/30 hover:shadow-md transition-all group"
                data-testid={`matlamat-card-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-[#D71920]/8 rounded flex items-center justify-center text-[#D71920] group-hover:bg-[#D71920] group-hover:text-white transition-colors">
                    {m.icon}
                  </div>
                  <span className="text-xs font-extrabold text-[#E5E5E5] tracking-widest">{m.num}</span>
                </div>
                <h3 className="text-sm font-bold text-[#0B0B0B] mb-2 leading-tight">{m.title}</h3>
                <p className="text-xs text-[#777] leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. WILAYAH RSS ───────────────────────────────────────── */}
      <section className="bg-white py-20 sm:py-28" data-testid="wilayah-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-12 gap-4">
            <div>
              <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-3">Liputan Negeri</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]">13 Wilayah RSS Sarawak</h2>
              <p className="text-[#555] mt-2 text-sm max-w-lg">
                RSS beroperasi merentasi 13 wilayah, memastikan seni silat berkembang di setiap pelusuk negeri Sarawak.
              </p>
            </div>
            <Link to="/wilayah" className="text-sm font-bold text-[#D71920] hover:underline flex items-center gap-1 whitespace-nowrap">
              Lihat Semua Wilayah <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {wilayahList.map((w, i) => (
              <div
                key={i}
                className="border border-[#E5E5E5] rounded p-4 text-center hover:border-[#D71920]/30 hover:shadow-sm transition-all"
                data-testid={`wilayah-chip-${i}`}
              >
                <div
                  className="w-7 h-7 rounded-full bg-[#D71920]/8 border border-[#D71920]/20 flex items-center justify-center font-bold text-[#D71920] text-xs mx-auto mb-2"
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="text-xs font-bold text-[#0B0B0B] leading-tight">{w}</div>
                <div className="text-[10px] text-[#999] mt-0.5">RSS Wilayah</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. KEPIMPINAN PREVIEW ────────────────────────────────── */}
      <section className="bg-[#0B0B0B] text-white py-20 sm:py-28" data-testid="kepimpinan-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-12 gap-4">
            <div>
              <div className="text-xs font-bold tracking-widest text-[#FFD21F] uppercase mb-3">Barisan Kepimpinan</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white">Kepimpinan RSS Sarawak</h2>
            </div>
            <Link to="/kepimpinan" className="text-sm font-bold text-[#FFD21F] hover:underline flex items-center gap-1 whitespace-nowrap">
              Lihat Kepimpinan Penuh <ArrowRight size={14} />
            </Link>
          </div>

          {/* Penaung */}
          <div className="mb-6 border border-white/10 rounded p-6 bg-white/5">
            <div className="text-[10px] font-bold tracking-widest text-[#FFD21F] uppercase mb-2">Penaung</div>
            <div className="text-base sm:text-lg font-extrabold text-white leading-tight">
              Yang Amat Berhormat Datuk Patinggi Tan Sri (Dr)
            </div>
            <div className="text-lg sm:text-xl font-extrabold text-[#FFD21F] leading-tight mt-0.5">
              Abang Haji Abdul Rahman Zohari Bin Tun Datuk Abang Haji Openg
            </div>
            <div className="text-sm text-[#999] mt-1">Premier Sarawak</div>
          </div>

          {/* MKT Preview */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="mkt-preview">
            {kepimpinanPreview.map((k, i) => (
              <div
                key={i}
                className={`rounded p-5 border transition-colors ${
                  k.level === 'primary'
                    ? 'border-[#D71920]/40 bg-[#D71920]/10'
                    : 'border-white/10 bg-white/5'
                }`}
                data-testid={`kepimpinan-card-${i}`}
              >
                <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-4">
                  <Users size={20} className="text-white/60" />
                </div>
                <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${k.level === 'primary' ? 'text-[#FFD21F]' : 'text-[#999]'}`}>
                  {k.position}
                </div>
                <div className="text-sm font-bold text-white leading-tight">{k.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. BIRO RSS ──────────────────────────────────────────── */}
      <section className="bg-white py-20 sm:py-28" data-testid="biro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-3">Bahagian Teknikal</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B] mb-10">Biro Rumpun Silat Sarawak</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {biroData.map((b, i) => (
              <div
                key={i}
                className="border border-[#E5E5E5] rounded overflow-hidden hover:shadow-md transition-shadow"
                style={{ borderTopColor: b.color, borderTopWidth: 3 }}
                data-testid={`biro-card-${i}`}
              >
                <div className="p-6">
                  <h3 className="font-extrabold text-[#0B0B0B] text-base mb-3">{b.name}</h3>
                  <p className="text-xs text-[#777] leading-relaxed mb-5">{b.desc}</p>
                  <div>
                    <div className="text-[10px] font-bold text-[#999] uppercase tracking-wide mb-0.5">Ketua Penyelaras</div>
                    <div className="text-xs font-semibold text-[#0B0B0B]">{b.ketua}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. PENCAPAIAN ────────────────────────────────────────── */}
      <section className="bg-[#F7F7F7] py-20 sm:py-28" data-testid="pencapaian-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
            <div>
              <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-3">Rekod Kejayaan</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]">Pencapaian RSS Sarawak</h2>
            </div>
            <Link to="/pencapaian" className="text-sm font-bold text-[#D71920] hover:underline flex items-center gap-1 whitespace-nowrap">
              Lihat Semua Pencapaian <ArrowRight size={14} />
            </Link>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8">
            {[{ key: 'sukma', label: 'SUKMA & Kebangsaan', icon: <Trophy size={13} /> }, { key: 'intl', label: 'Antarabangsa', icon: <Globe size={13} /> }].map(t => (
              <button
                key={t.key}
                onClick={() => setAchievTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded border transition-colors ${
                  achievTab === t.key
                    ? 'bg-[#0B0B0B] text-white border-[#0B0B0B]'
                    : 'bg-white text-[#555] border-[#E5E5E5] hover:border-[#0B0B0B]'
                }`}
              >
                {t.icon}{t.label}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(achievTab === 'sukma' ? sukmaHighlights : intlHighlights).map((item, i) => {
              const c = tierColor[item.tier] || tierColor.gold;
              return (
                <div
                  key={i}
                  className="bg-white rounded border overflow-hidden hover:shadow-md transition-shadow"
                  style={{ borderColor: c.border }}
                  data-testid={`pencapaian-card-${i}`}
                >
                  <div className="h-1" style={{ backgroundColor: c.border }} />
                  <div className="p-5">
                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded text-white inline-block mb-3"
                      style={{ backgroundColor: c.text }}>
                      {item.year}
                    </span>
                    <h3 className="font-bold text-[#0B0B0B] text-sm mb-1">{item.event}</h3>
                    {item.venue && <p className="text-xs text-[#777] mb-2">{item.venue}</p>}
                    <div
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full mt-1"
                      style={{ backgroundColor: c.bg, color: c.text }}
                    >
                      <Award size={11} />{item.result}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 9. PROGRAM BERSKALA BESAR ────────────────────────────── */}
      <section className="bg-white py-20 sm:py-28" data-testid="program-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-3">Sejarah Program</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B] mb-12">Program Berskala Besar RSS</h2>
          <div className="relative">
            {/* Timeline line */}
            <div className="hidden lg:block absolute left-[88px] top-0 bottom-0 w-px bg-[#E5E5E5]" />
            <div className="space-y-4">
              {programBesar.map((p, i) => (
                <div key={i} className="flex gap-5 items-start" data-testid={`program-item-${i}`}>
                  <div className="flex-shrink-0 w-20 text-right">
                    <span className="inline-block bg-[#D71920] text-white text-xs font-extrabold px-2 py-1 rounded">
                      {p.year}
                    </span>
                  </div>
                  <div className="hidden lg:flex items-start pt-2.5">
                    <div className="w-3 h-3 rounded-full bg-white border-2 border-[#D71920] flex-shrink-0" />
                  </div>
                  <div className="flex-1 bg-[#F7F7F7] border border-[#E5E5E5] rounded p-4 hover:border-[#D71920]/30 transition-colors">
                    <span className="text-[10px] font-bold text-[#D71920] uppercase tracking-wide">{p.cat}</span>
                    <p className="font-semibold text-[#0B0B0B] text-sm mt-1 leading-tight">{p.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 10. AKTIVITI PREVIEW ─────────────────────────────────── */}
      <section className="bg-[#F7F7F7] py-20 sm:py-28" data-testid="aktiviti-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-12 gap-4">
            <div>
              <div className="text-xs font-bold tracking-widest text-[#D71920] uppercase mb-3">Program & Aktiviti</div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]">Aktiviti Rumpun Silat Sarawak</h2>
            </div>
            <Link to="/aktiviti" className="text-sm font-bold text-[#D71920] hover:underline flex items-center gap-1 whitespace-nowrap">
              Semua Aktiviti <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {aktivitiCards.map((a, i) => (
              <div
                key={i}
                className="bg-white border border-[#E5E5E5] p-6 rounded hover:border-[#D71920]/30 hover:shadow-sm transition-all"
                data-testid={`aktiviti-card-${i}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-1.5 bg-[#D71920] rounded-full flex-shrink-0" />
                  <span className="text-[10px] font-bold text-[#D71920] uppercase tracking-wide">{a.cat}</span>
                </div>
                <h3 className="font-bold text-[#0B0B0B] text-sm leading-snug">{a.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 11. DIGITAL PORTAL (supporting only) ─────────────────── */}
      <section className="bg-[#0B0B0B] text-white py-14 sm:py-18" data-testid="digital-portal-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-xs font-bold tracking-widest text-[#FFD21F] uppercase mb-4">Portal Digital</div>
          <h2 className="text-xl sm:text-2xl font-extrabold text-white mb-4">
            Portal Digital Rumpun Silat Sarawak
          </h2>
          <p className="text-[#777] text-sm leading-relaxed mb-8 max-w-2xl mx-auto">
            RSS turut membangunkan portal digital bagi memudahkan pendaftaran perguruan, rekod ahli dan pesilat,
            direktori perguruan, aktiviti, pertandingan, pengesahan data serta penyelarasan pentadbiran.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {user ? (
              <button
                onClick={toDashboard}
                className="bg-[#FFD21F] hover:bg-[#f5c800] text-[#0B0B0B] text-sm font-bold px-5 py-2.5 rounded transition-colors"
                data-testid="portal-dashboard-btn"
              >
                Ke Dashboard
              </button>
            ) : (
              <Link to="/portal-ahli" className="bg-[#FFD21F] hover:bg-[#f5c800] text-[#0B0B0B] text-sm font-bold px-5 py-2.5 rounded transition-colors" data-testid="portal-login-btn">
                Portal Ahli
              </Link>
            )}
            <Link to="/register-school" className="border border-white/30 hover:border-white text-white text-sm font-semibold px-5 py-2.5 rounded transition-colors" data-testid="portal-daftar-perguruan-btn">
              Daftar Perguruan
            </Link>
            <Link to="/register-member" className="border border-white/30 hover:border-white text-white text-sm font-semibold px-5 py-2.5 rounded transition-colors" data-testid="portal-daftar-pesilat-btn">
              Daftar Ahli
            </Link>
          </div>
        </div>
      </section>

      {/* ── 12. IKRAR AHLI ───────────────────────────────────────── */}
      <section
        className="py-20 sm:py-28 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #D71920 0%, #a8121a 100%)' }}
        data-testid="ikrar-section"
      >
        {/* Decorative */}
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center text-white">
          <div className="text-xs font-bold tracking-widest uppercase mb-5 text-white/70">
            Ikrar Ahli Rumpun Silat Sarawak
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-6 leading-tight">
            "Kami berjanji, akan taat setia kepada kepimpinan Rumpun Silat Sarawak."
          </h2>
          <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            Memartabatkan seni silat sebagai wadah perjuangan demi Agama, Bangsa dan Negara —
            dengan membela kebenaran, menjaga maruah bangsa, dan membangun generasi pesilat yang
            bermaruah, bersatu-padu, sihat dan berdisiplin.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/tentang-rss"
              className="bg-white text-[#D71920] hover:bg-[#F7F7F7] text-sm font-bold px-6 py-3 rounded transition-colors"
              data-testid="ikrar-kenali-rss-btn"
            >
              Kenali RSS Selanjutnya
            </Link>
            <Link
              to="/direktori-perguruan"
              className="border-2 border-white/50 hover:border-white text-white text-sm font-semibold px-6 py-3 rounded transition-colors"
              data-testid="ikrar-direktori-btn"
            >
              Direktori Perguruan
            </Link>
          </div>
          <div className="mt-10 pt-8 border-t border-white/20">
            <p className="text-xs font-extrabold tracking-[0.4em] text-white/70 uppercase">
              Berseni · Berbudi · Berbakti
            </p>
          </div>
        </div>
      </section>

    </PublicLayout>
  );
}
